import React, { useState } from "react";
import { MapPin, Phone, Mail, Facebook, Send, Check } from "lucide-react";
import api from "../../lib/api";
import { BRAND } from "../../lib/brand";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: "" });
    try {
      await api.post("/contact", form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong";
      setStatus({ state: "error", error: msg });
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-cream" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-brand-blue font-heading font-bold tracking-wider uppercase text-xs">Contact</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">We'd love to hear from you</h2>
          <p className="mt-3 text-[color:var(--text-secondary)]">
            Have a question, want a school visit, or just want to chat? Reach out below.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-4">
            <InfoCard icon={MapPin} label="Visit us" value={BRAND.address} />
            <InfoCard icon={Phone} label="Call" value={BRAND.phone} href={`tel:${BRAND.phone}`} />
            <InfoCard icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} />
            <InfoCard icon={Facebook} label="Facebook" value="@InnovativePreschoolPanga" href={BRAND.facebook} external />

            <div className="card-soft overflow-hidden bg-white" data-testid="contact-map">
              <iframe
                title="Map"
                src={BRAND.mapEmbed}
                className="w-full h-64 border-0"
                loading="lazy"
              />
              <a
                href={BRAND.mapShareUrl}
                target="_blank"
                rel="noreferrer"
                className="block px-5 py-3 text-sm font-semibold text-brand-blue hover:bg-light-blue transition border-t border-[#eee]"
                data-testid="contact-map-directions"
              >
                Get directions on Google Maps →
              </a>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 card-soft bg-white p-7 sm:p-9 space-y-4"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your name" required>
                <input
                  required value={form.name} onChange={onChange("name")}
                  className="input" data-testid="contact-name" placeholder="Sunita Sharma"
                />
              </Field>
              <Field label="Email" required>
                <input
                  required type="email" value={form.email} onChange={onChange("email")}
                  className="input" data-testid="contact-email" placeholder="you@example.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  value={form.phone} onChange={onChange("phone")}
                  className="input" data-testid="contact-phone" placeholder="+977..."
                />
              </Field>
              <Field label="Subject">
                <select className="input" value={form.subject} onChange={onChange("subject")} data-testid="contact-subject">
                  <option>General Inquiry</option>
                  <option>Admissions</option>
                  <option>School Visit</option>
                  <option>Other</option>
                </select>
              </Field>
            </div>
            <Field label="Message" required>
              <textarea
                required value={form.message} onChange={onChange("message")}
                rows={5} className="input resize-none" data-testid="contact-message"
                placeholder="Tell us how we can help..."
              />
            </Field>

            {status.state === "error" && (
              <div className="bg-red-50 text-red-700 rounded-2xl px-4 py-3 text-sm" data-testid="contact-error">
                {status.error}
              </div>
            )}
            {status.state === "success" && (
              <div className="bg-light-green text-[#1d6b32] rounded-2xl px-4 py-3 text-sm flex items-center gap-2" data-testid="contact-success">
                <Check size={16} /> Thank you! We'll get back to you soon.
              </div>
            )}

            <button type="submit" disabled={status.state === "submitting"} className="btn-primary w-full justify-center" data-testid="contact-submit">
              {status.state === "submitting" ? "Sending..." : <>Send Message <Send size={16} /></>}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: var(--bg-cream);
          border: 1px solid #e6e2d8;
          border-radius: 1rem;
          padding: 0.75rem 1rem;
          font-family: 'Nunito', sans-serif;
          font-size: 0.95rem;
          color: var(--text-primary);
          transition: border-color .15s, background .15s;
        }
        .input:focus { outline: none; background: white; border-color: var(--brand-blue); }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-[color:var(--text-primary)] mb-1.5">
      {label} {required && <span className="text-brand-blue">*</span>}
    </span>
    {children}
  </label>
);

const InfoCard = ({ icon: Icon, label, value, href, external }) => {
  const Content = (
    <div className="card-soft bg-white p-5 flex items-center gap-4" data-testid={`contact-info-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-12 h-12 rounded-2xl bg-light-blue flex items-center justify-center flex-shrink-0">
        <Icon className="text-brand-blue" size={20} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-[color:var(--text-secondary)]">{label}</div>
        <div className="font-heading font-bold text-[color:var(--text-primary)]">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>{Content}</a>
  ) : Content;
};

export default Contact;
